import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Play,
  Pause,
  Clock,
  CheckCircle,
  AlertCircle,
  Activity,
  RotateCcw,
  User as UserIcon,
  ChevronDown,
  XCircle,
  PlayCircle,
  Zap,
  GitBranch,
  Shield
} from 'lucide-react';
import { enhancedWorkflows } from '../../data/enhancedAgentsData';
import { Workflow } from '../../types';

// Reuse the same interfaces and mock data from the original
interface HistoryEntry {
  id: string;
  executionId: string;
  startTime: Date;
  endTime: Date;
  duration: number;
  status: 'completed' | 'failed' | 'cancelled';
  triggeredBy: string;
  stepsCompleted: number;
  totalSteps: number;
  errorMessage?: string;
  orchestrationType: 'native' | 'hosted' | 'hybrid';
}

interface ExecutionStep {
  id: string;
  name: string;
  type: 'task' | 'decision' | 'fork' | 'join';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  retryCount?: number;
  data?: any;
}

// Mock history data with orchestration types
const mockHistory: HistoryEntry[] = [
  {
    id: '1',
    executionId: 'ca93aef1-fb50-4b87-ae09-82506ff81bee',
    startTime: new Date(Date.now() - 7200000),
    endTime: new Date(Date.now() - 6600000),
    duration: 433000,
    status: 'completed',
    triggeredBy: 'API - Broker Portal',
    stepsCompleted: 14,
    totalSteps: 14,
    orchestrationType: 'native'
  },
  {
    id: '2',
    executionId: 'ba84def2-gb41-5c98-bf10-93617gg92cff',
    startTime: new Date(Date.now() - 14400000),
    endTime: new Date(Date.now() - 13800000),
    duration: 445000,
    status: 'completed',
    triggeredBy: 'john.smith@insurance.com',
    stepsCompleted: 14,
    totalSteps: 14,
    orchestrationType: 'native'
  },
  {
    id: '3',
    executionId: 'g23ccg3-9e72-g21-a4728hh03d',
    startTime: new Date(Date.now() - 10800000),
    endTime: new Date(Date.now() - 10740000),
    duration: 456000,
    status: 'failed',
    triggeredBy: 'scheduler',
    stepsCompleted: 5,
    totalSteps: 14,
    errorMessage: 'Agent service timeout',
    orchestrationType: 'native'
  }
];

// Task Details Panel Component (inline, not modal)
const TaskDetailsPanel: React.FC<{
  taskData: any;
}> = ({ taskData }) => {
  const [activeSubTab, setActiveSubTab] = useState<'summary' | 'input' | 'output' | 'logs'>('summary');
  
  return (
    <div style={{ 
      background: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '16px 20px', 
        borderBottom: '1px solid #e0e0e0',
        background: '#f8f9fa'
      }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>
          {taskData.summary.taskName}
        </h4>
        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
          {taskData.summary.taskType} • {taskData.summary.status} • {taskData.summary.duration}
        </div>
      </div>

      {/* Sub Tabs */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '1px solid #e0e0e0',
        background: '#f8f9fa'
      }}>
        {(['summary', 'input', 'output', 'logs'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveSubTab(tab)}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: activeSubTab === tab ? 'white' : 'transparent',
              borderBottom: activeSubTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: activeSubTab === tab ? '600' : '400',
              textTransform: 'capitalize',
              color: activeSubTab === tab ? '#3b82f6' : '#666'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {activeSubTab === 'summary' && (
          <div style={{ fontSize: '12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {Object.entries(taskData.summary).map(([key, value]) => (
                  <tr key={key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ 
                      padding: '6px 8px', 
                      fontWeight: '600', 
                      width: '120px',
                      textTransform: 'capitalize',
                      fontSize: '11px'
                    }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </td>
                    <td style={{ 
                      padding: '6px 8px', 
                      fontFamily: key.includes('Id') ? 'monospace' : 'inherit',
                      fontSize: '11px'
                    }}>
                      {String(value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeSubTab === 'input' && (
          <div style={{ 
            background: '#263238', 
            color: '#aed581',
            padding: '12px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '10px',
            overflow: 'auto',
            maxHeight: '400px'
          }}>
            <pre>{JSON.stringify(taskData.input, null, 2)}</pre>
          </div>
        )}

        {activeSubTab === 'output' && (
          <div style={{ 
            background: '#263238', 
            color: '#aed581',
            padding: '12px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '10px',
            overflow: 'auto',
            maxHeight: '400px'
          }}>
            <pre>{JSON.stringify(taskData.output, null, 2)}</pre>
          </div>
        )}

        {activeSubTab === 'logs' && (
          <div style={{ fontSize: '11px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '600', fontSize: '10px' }}>Timestamp</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '600', fontSize: '10px' }}>Entry</th>
                </tr>
              </thead>
              <tbody>
                {taskData.logs.map((log: any, idx: number) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '9px' }}>
                      {log.timestamp}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: '10px' }}>
                      {log.entry}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// NEW: Compact Workflow Execution View with fixed sidebar width
const CompactWorkflowExecutionView: React.FC<{ workflow: Workflow }> = ({ workflow }) => {
  const [executionSteps, setExecutionSteps] = useState<ExecutionStep[]>([
    { id: 'validate_auth_token', name: 'Validate Authentication Token', type: 'task', status: 'completed', startTime: new Date(Date.now() - 15000), endTime: new Date(Date.now() - 14766), duration: 234 },
    { id: 'decision_agent_rerun', name: 'Decision Agent Rerun', type: 'decision', status: 'completed', startTime: new Date(Date.now() - 14750), endTime: new Date(Date.now() - 14705), duration: 45 },
    { id: 'generate_auth_token', name: 'Generate Auth Token', type: 'task', status: 'completed', startTime: new Date(Date.now() - 14700), endTime: new Date(Date.now() - 14544), duration: 156 },
    { id: 'poll_submission_status', name: 'Poll Submission Status', type: 'task', status: 'running', startTime: new Date(Date.now() - 14500), retryCount: 3, data: { currentRetry: 3, maxRetries: 5 } },
    { id: 'fetch_submission_data', name: 'Fetch Submission Data', type: 'task', status: 'pending' },
    { id: 'call_agent_service', name: 'Call Agent Service', type: 'task', status: 'pending' },
    { id: 'send_to_service_now', name: 'Send to ServiceNow', type: 'task', status: 'pending' },
    { id: 'cleanup_eml_file', name: 'Cleanup EML File', type: 'task', status: 'pending' },
  ]);

  const [isExecuting, setIsExecuting] = useState(true);

  const getOrchestrationInfo = (type: string) => {
    switch (type) {
      case 'native':
        return {
          label: 'Native Agent Orchestration',
          color: '#3b82f6',
          icon: <Zap size={16} />,
          description: 'ElevateNow certified agents'
        };
      case 'hosted':
        return {
          label: 'Hosted Agent Integration',
          color: '#9ca3af',
          icon: <GitBranch size={16} />,
          description: 'AWS Agent Code integration'
        };
      case 'hybrid':
        return {
          label: 'Hybrid Intelligence',
          color: '#3b82f6',
          icon: <Shield size={16} />,
          description: 'Native + hosted coordination'
        };
      default:
        return {
          label: 'Standard Workflow',
          color: 'var(--text-secondary)',
          icon: <Play size={16} />,
          description: 'Basic orchestration'
        };
    }
  };

  const orchestration = getOrchestrationInfo(workflow?.orchestrationType || 'native');

  // Simulate live execution updates
  useEffect(() => {
    if (!isExecuting) return;
    
    const interval = setInterval(() => {
      setExecutionSteps(prev => prev.map((step, idx) => {
        if (step.status === 'running' && Math.random() > 0.7) {
          return {
            ...step,
            status: 'completed',
            endTime: new Date(),
            duration: Math.floor(Math.random() * 200) + 50
          };
        }
        if (step.status === 'pending' && idx > 0 && prev[idx - 1].status === 'completed') {
          return {
            ...step,
            status: 'running',
            startTime: new Date()
          };
        }
        return step;
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, [isExecuting]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} style={{ color: 'var(--status-active)' }} />;
      case 'running':
        return <Activity size={16} style={{ color: orchestration.color }} className="agent-active" />;
      case 'failed':
        return <XCircle size={16} style={{ color: 'var(--status-error)' }} />;
      case 'pending':
        return <Clock size={16} style={{ color: 'var(--text-muted)' }} />;
      default:
        return <AlertCircle size={16} style={{ color: 'var(--text-muted)' }} />;
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  return (
    // KEY CHANGE: Fixed sidebar width to 350px instead of flexible grid
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '20px', height: '100%' }}>
      {/* Main Execution Flow - Takes remaining space */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', overflow: 'hidden' }}>
        {/* Execution Header and Controls */}
        <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Activity size={18} style={{ color: orchestration.color }} />
              <span style={{ fontWeight: '600' }}>Live Execution - {workflow.name}</span>
              {isExecuting && (
                <div style={{
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: '600',
                  background: orchestration.color,
                  color: 'white'
                }}>
                  RUNNING
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ padding: '6px 12px', border: '1px solid #e0e0e0', background: 'white', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RotateCcw size={14} />
                Retry
              </button>
              <button 
                onClick={() => setIsExecuting(!isExecuting)}
                style={{ padding: '6px 12px', border: '1px solid #e0e0e0', background: 'white', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {isExecuting ? (
                  <>
                    <Pause size={14} />
                    Pause
                  </>
                ) : (
                  <>
                    <Play size={14} />
                    Resume
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Compact Pipeline View */}
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflowX: 'auto', paddingBottom: '4px' }}>
              {executionSteps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div style={{
                    minWidth: '140px',
                    padding: '12px',
                    background: step.status === 'completed' ? '#e8f5e9' : step.status === 'running' ? '#e3f2fd' : '#f5f5f5',
                    border: `2px solid ${
                      step.status === 'completed' ? 'var(--status-active)' :
                      step.status === 'running' ? orchestration.color :
                      step.status === 'failed' ? 'var(--status-error)' :
                      'var(--border-color)'
                    }`,
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontSize: '10px',
                    position: 'relative'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px' }}>
                      {getStatusIcon(step.status)}
                    </div>
                    <div style={{ fontWeight: '600', marginBottom: '4px', lineHeight: '1.2' }}>
                      {step.name}
                    </div>
                    {step.retryCount && (
                      <div style={{ color: 'var(--status-warning)', fontSize: '9px' }}>
                        Retry: {step.retryCount}
                      </div>
                    )}
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      {step.duration ? formatDuration(step.duration) : 
                       step.status === 'running' ? `${Math.floor((Date.now() - (step.startTime?.getTime() || 0)) / 1000)}s` : 
                       'Waiting...'}
                    </div>
                  </div>
                  {index < executionSteps.length - 1 && (
                    <div style={{
                      color: step.status === 'completed' ? orchestration.color : '#999',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}>→</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Execution Timeline */}
        <div style={{ flex: 1, background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0', fontWeight: '600' }}>
            Execution Timeline
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <div style={{ fontSize: '14px' }}>
              {executionSteps.map((step) => (
                <div
                  key={step.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    background: step.status === 'running' ? `${orchestration.color}08` : 'transparent',
                    borderLeft: `4px solid ${
                      step.status === 'completed' ? 'var(--status-active)' :
                      step.status === 'running' ? orchestration.color :
                      step.status === 'failed' ? 'var(--status-error)' :
                      'var(--border-color)'
                    }`
                  }}
                >
                  <div style={{ marginRight: '12px' }}>
                    {getStatusIcon(step.status)}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                      {step.name}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {step.startTime && `Started: ${step.startTime.toLocaleTimeString()}`}
                      {step.retryCount && step.retryCount > 0 && (
                        <span style={{ color: 'var(--status-warning)', marginLeft: '12px' }}>
                          • Retry #{step.retryCount}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'right' }}>
                    {step.duration ? formatDuration(step.duration) : 
                     step.status === 'running' ? (
                       <span style={{ color: orchestration.color }}>
                         {Math.floor((Date.now() - (step.startTime?.getTime() || 0)) / 1000)}s
                       </span>
                     ) : '-'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Width Sidebar - Execution Summary */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '350px' }}>
        {/* Execution Summary */}
        <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <div style={{ padding: '12px', borderBottom: '1px solid #e0e0e0', background: '#e3f2fd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '600', fontSize: '14px' }}>Execution Summary</span>
              <span style={{ fontSize: '10px', color: orchestration.color, fontWeight: '600' }}>
                {isExecuting ? 'RUNNING' : 'PAUSED'}
              </span>
            </div>
          </div>
          <div style={{ padding: '12px' }}>
            <div style={{ fontSize: '13px' }}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Workflow ID:</div>
                <div style={{ fontFamily: 'monospace', fontSize: '11px', color: orchestration.color, wordBreak: 'break-all' }}>
                  e03aaf1-7c50-e09
                </div>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Started:</div>
                <div>10:25:43 AM</div>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Duration:</div>
                <div>7m 12s</div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>Progress</div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>
                    {executionSteps.filter(s => s.status === 'completed').length}/8
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                    steps completed
                  </span>
                </div>
                <div className="progress-bar" style={{ marginBottom: '8px' }}>
                  <div 
                    className="progress-fill"
                    style={{
                      width: `${(executionSteps.filter(s => s.status === 'completed').length / executionSteps.length) * 100}%`,
                      background: orchestration.color
                    }}
                  />
                </div>
              </div>

              <div>
                <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Current Step:</div>
                <div style={{ fontSize: '12px', fontWeight: '600' }}>
                  {executionSteps.find(s => s.status === 'running')?.name || 'Waiting for completion'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  {executionSteps.find(s => s.status === 'running') ? 'In progress' : 'Workflow complete'}
                </div>
              </div>

              {executionSteps.find(s => s.retryCount && s.retryCount > 0) && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Retry Status:</div>
                  <div style={{ fontSize: '12px', color: 'var(--status-warning)' }}>
                    Attempt #{executionSteps.find(s => s.retryCount && s.retryCount > 0)?.retryCount}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Agent Results */}
        <div style={{ flex: 1, background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '12px', borderBottom: '1px solid #e0e0e0', fontWeight: '600', fontSize: '14px' }}>
            Agent Results
          </div>
          <div style={{ flex: 1, padding: '12px', fontSize: '12px', overflowY: 'auto' }}>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: '600', color: 'var(--status-active)', marginBottom: '6px' }}>
                • InsurVerify: Complete
              </div>
              <div style={{ paddingLeft: '12px', color: 'var(--text-secondary)' }}>
                Validation score: 94.2%<br/>
                Fields processed: 47/50
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: '600', color: 'var(--status-active)', marginBottom: '6px' }}>
                • ExposureInsights: Complete  
              </div>
              <div style={{ paddingLeft: '12px', color: 'var(--text-secondary)' }}>
                CAT exposure: $2.4M PML<br/>
                Risk zones: 3 identified
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: '600', color: 'var(--status-active)', marginBottom: '6px' }}>
                • LossInsights: Complete
              </div>
              <div style={{ paddingLeft: '12px', color: 'var(--text-secondary)' }}>
                Loss ratio: 0.67<br/>
                Trend: Improving
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: '600', color: orchestration.color, marginBottom: '6px' }}>
                • EligibilityChecker: Processing
              </div>
              <div style={{ paddingLeft: '12px', color: 'var(--text-secondary)' }}>
                Appetite matching...<br/>
                Guidelines: 15/18 checked
              </div>
            </div>

            <div style={{ color: 'var(--text-muted)' }}>
              <div style={{ fontWeight: '600', marginBottom: '6px' }}>
                • Remaining agents: Pending
              </div>
              <div style={{ paddingLeft: '12px' }}>
                Waiting for prerequisites
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// History View Component
const HistoryView: React.FC<{ workflow: Workflow }> = ({ workflow }) => {
  const formatDuration = (ms: number) => {
    if (ms < 60000) return `${Math.floor(ms / 1000)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle size={16} color="#28a745" />;
      case 'failed': return <AlertCircle size={16} color="#dc3545" />;
      case 'cancelled': return <AlertCircle size={16} color="#6c757d" />;
      default: return <Clock size={16} color="#6c757d" />;
    }
  };

  const getOrchestrationInfo = (type: string) => {
    switch (type) {
      case 'native':
        return { color: '#3b82f6', label: 'Native' };
      case 'hosted':
        return { color: '#9ca3af', label: 'Hosted' };
      case 'hybrid':
        return { color: '#3b82f6', label: 'Hybrid' };
      default:
        return { color: 'var(--text-secondary)', label: 'Standard' };
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '24px',
        padding: '16px',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
            Date Range
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input type="date" style={{
              padding: '6px 12px',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              fontSize: '13px',
            }} />
            <span style={{ alignSelf: 'center' }}>to</span>
            <input type="date" style={{
              padding: '6px 12px',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              fontSize: '13px',
            }} />
          </div>
        </div>
        
        <div>
          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
            Status
          </label>
          <select style={{
            padding: '6px 12px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            fontSize: '13px',
            background: 'white',
          }}>
            <option>All</option>
            <option>Completed</option>
            <option>Failed</option>
            <option>Cancelled</option>
          </select>
        </div>
        
        <div style={{ alignSelf: 'flex-end' }}>
          <button style={{
            padding: '6px 16px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '13px',
            cursor: 'pointer',
          }}>
            Apply Filters
          </button>
        </div>
      </div>

      {/* History Table */}
      <div style={{ 
        background: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>
                Execution ID
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>
                Status
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>
                Start Time
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>
                Duration
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>
                Progress
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>
                Type
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>
                Triggered By
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#666' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {mockHistory.map((entry) => {
              const orchestrationInfo = getOrchestrationInfo(entry.orchestrationType);
              return (
                <tr key={entry.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '16px', fontSize: '13px' }}>
                    <div style={{ fontFamily: 'monospace', color: '#3b82f6', cursor: 'pointer' }}>
                      {entry.executionId.substring(0, 8)}...
                    </div>
                  </td>
                  <td style={{ padding: '16px', fontSize: '13px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {getStatusIcon(entry.status)}
                      <span style={{ textTransform: 'capitalize' }}>{entry.status}</span>
                    </div>
                  </td>
                  <td style={{ padding: '16px', fontSize: '13px' }}>
                    <div>{entry.startTime.toLocaleDateString()}</div>
                    <div style={{ fontSize: '11px', color: '#666' }}>{entry.startTime.toLocaleTimeString()}</div>
                  </td>
                  <td style={{ padding: '16px', fontSize: '13px' }}>
                    {formatDuration(entry.duration)}
                  </td>
                  <td style={{ padding: '16px', fontSize: '13px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ 
                        width: '100px', 
                        height: '6px', 
                        background: '#e0e0e0', 
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${(entry.stepsCompleted / entry.totalSteps) * 100}%`,
                          height: '100%',
                          background: entry.status === 'completed' ? '#28a745' : '#dc3545',
                        }} />
                      </div>
                      <span style={{ fontSize: '11px', color: '#666' }}>
                        {entry.stepsCompleted}/{entry.totalSteps}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '16px', fontSize: '13px' }}>
                    <div style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: '600',
                      background: orchestrationInfo.color,
                      color: 'white',
                      display: 'inline-block'
                    }}>
                      {orchestrationInfo.label.toUpperCase()}
                    </div>
                  </td>
                  <td style={{ padding: '16px', fontSize: '13px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <UserIcon size={14} color="#666" />
                      <span>{entry.triggeredBy}</span>
                    </div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <button style={{
                      padding: '4px 8px',
                      background: 'transparent',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}>
                      View Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {/* Error messages for failed entries */}
        {mockHistory.map((entry) => (
          entry.errorMessage && (
            <div key={`error-${entry.id}`} style={{ 
              padding: '8px 12px',
              background: 'var(--status-error)15',
              borderRadius: '4px',
              fontSize: '12px',
              color: 'var(--status-error)',
              margin: '8px 16px'
            }}>
              Error: {entry.errorMessage}
            </div>
          )
        ))}
      </div>
    </div>
  );
};

// FIXED: Design View Component with proper fixed width layout
const CompactDesignView: React.FC<{ workflow: Workflow }> = ({ workflow }) => {
  const [selectedStep, setSelectedStep] = useState<any>(null);
  
  // Mock conductor task data
  const mockTaskData = {
    'step1': {
      summary: {
        taskType: 'SIMPLE',
        status: 'COMPLETED',
        taskName: 'validate_auth_token',
        taskReference: 'validate_auth_token',
        taskExecutionId: '3ffd81ab-7432-4818-8849-2c641b54b090',
        retryCount: 0,
        scheduledTime: '2025-07-21 11:24:12.152',
        startTime: '2025-07-21 11:24:12.233',
        endTime: '2025-07-21 11:24:12.602',
        duration: '369ms',
        worker: 'conductor-workers-7f7df59b46-8skkz'
      },
      input: {
        auth_token: "test",
        case_id: "SUB0010008",
        validate_expiry: true,
        timeout_seconds: 30
      },
      output: {
        status: "COMPLETED",
        outputData: {
          token_valid: true,
          expires_at: "2025-07-22T11:24:12Z",
          user_id: "system_api"
        },
        logs: [
          "Token validation started",
          "Token format valid",
          "Token not expired",
          "Validation completed successfully"
        ]
      },
      logs: [
        { timestamp: '2025-07-21 11:24:12', entry: 'validate_auth_token_worker for task: 3ffd81ab-7432-4818-8849-2c641b54b090' },
        { timestamp: '2025-07-21 11:24:12', entry: 'Processing auth token validation' },
        { timestamp: '2025-07-21 11:24:12', entry: 'Token format verified' },
        { timestamp: '2025-07-21 11:24:12', entry: 'Token expiry checked - valid until 2025-07-22T11:24:12Z' },
        { timestamp: '2025-07-21 11:24:12', entry: 'Task completed successfully' }
      ]
    },
    'step2': {
      summary: {
        taskType: 'SIMPLE',
        status: 'COMPLETED',
        taskName: 'extract_submission_data',
        taskReference: 'extract_submission_data',
        taskExecutionId: '4aae82bc-8543-5929-9950-3d752c65c101',
        retryCount: 1,
        scheduledTime: '2025-07-21 11:24:13.152',
        startTime: '2025-07-21 11:24:13.233',
        endTime: '2025-07-21 11:24:15.847',
        duration: '2.614s',
        worker: 'conductor-workers-7f7df59b46-8skkz'
      },
      input: {
        attachments: [
          {
            filename: "24-25 PROP Review Application Edited (1).pdf",
            attachment_data: "JVBERi0xLjUKJeLjz9MKMSAwIG9iao8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFI+PgplbmRvYmoKMiAwIG9iao8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PgplbmRvYmoK..."
          }
        ],
        email_body: "Please review the attached property application for renewal consideration..."
      },
      output: {
        status: "COMPLETED",
        outputData: {
          submission_id: "SUB0010008_b1fd552c-ea2e-4389-999a-fb8582abf720",
          extracted_data: {
            insured_name: "ABC Manufacturing Corp",
            policy_number: "POL-2024-789456",
            effective_date: "2025-01-01",
            expiry_date: "2026-01-01",
            coverage_amount: 5000000,
            property_address: "123 Industrial Blvd, Manufacturing City, ST 12345"
          }
        }
      },
      logs: [
        { timestamp: '2025-07-21 11:24:13', entry: 'extract_submission_data_worker for task: 4aae82bc-8543-5929-9950-3d752c65c101' },
        { timestamp: '2025-07-21 11:24:13', entry: 'Processing email attachment' },
        { timestamp: '2025-07-21 11:24:14', entry: 'PDF extraction started' },
        { timestamp: '2025-07-21 11:24:15', entry: 'Extracted policy details successfully' },
        { timestamp: '2025-07-21 11:24:15', entry: 'Task completed successfully' }
      ]
    }
  };

  return (
    // KEY FIX: Using minmax to ensure minimum width for task details and flex approach
    <div style={{ 
      display: 'flex', 
      gap: '24px', 
      height: '100%',
      minHeight: '600px'
    }}>
      {/* Workflow Pipeline - Takes remaining space but allows flexibility */}
      <div style={{ flex: 1, minWidth: '600px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
            Workflow Pipeline
          </h3>
          
          {/* Performance KPIs */}
          <div style={{ display: 'flex', gap: '20px', fontSize: '12px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                {workflow.metrics.avgCompletionTime} min
              </div>
              <div style={{ color: '#999' }}>Avg. Time</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#28a745' }}>
                {workflow.metrics.successRate}%
              </div>
              <div style={{ color: '#999' }}>Success</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                {workflow.metrics.totalExecutions.toLocaleString()}
              </div>
              <div style={{ color: '#999' }}>Total Runs</div>
            </div>
          </div>
        </div>
        
        <div style={{ 
          background: '#f8f9fa', 
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', overflowX: 'auto' }}>
            {workflow.steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div 
                  onClick={() => setSelectedStep(mockTaskData[step.id as keyof typeof mockTaskData])}
                  style={{
                    minWidth: '160px',
                    padding: '12px',
                    background: selectedStep === mockTaskData[step.id as keyof typeof mockTaskData] ? '#e3f2fd' : 'white',
                    border: selectedStep === mockTaskData[step.id as keyof typeof mockTaskData] ? '2px solid #3b82f6' : '1px solid #e0e0e0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (selectedStep !== mockTaskData[step.id as keyof typeof mockTaskData]) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedStep !== mockTaskData[step.id as keyof typeof mockTaskData]) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                    }
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '6px', fontSize: '13px' }}>
                    {step.name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
                    Agent: <span style={{ color: '#3b82f6', fontWeight: '500' }}>{step.agentId}</span>
                  </div>
                  
                  <div style={{ fontSize: '10px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                      {step.outputs.slice(0, 2).map(output => (
                        <span key={output} style={{ 
                          padding: '1px 4px', 
                          background: '#e3f2fd', 
                          borderRadius: '8px',
                          color: '#3b82f6',
                          fontSize: '9px'
                        }}>
                          {output}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                {index < workflow.steps.length - 1 && (
                  <div style={{ color: '#999', fontSize: '16px' }}>→</div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Participating AI Agents */}
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
            Participating AI Agents
          </h3>
          <div style={{ display: 'grid', gap: '8px' }}>
            {workflow.steps.map(step => {
              const agentInfo = {
                'insurverify': { desc: 'Validates data completeness and quality', icon: '🔍' },
                'exposureinsights': { desc: 'Analyzes exposure and CAT risks', icon: '📊' },
                'lossinsights': { desc: 'Consolidates and analyzes loss history', icon: '📈' },
                'eligibilitychecker': { desc: 'Checks appetite and eligibility rules', icon: '✅' },
                'insurancecontrolsadvisor': { desc: 'Monitors audit controls', icon: '🛡️' },
                'compliancechecker': { desc: 'Validates regulatory compliance', icon: '⚖️' },
                'marketintel': { desc: 'Enriches with market intelligence', icon: '🌐' },
                'riskscorer': { desc: 'Generates ML-based risk scores', icon: '🎯' },
                'documentextractor': { desc: 'Extracts data from documents', icon: '📄' },
              };
              
              const info = agentInfo[step.agentId as keyof typeof agentInfo] || { desc: 'Process data', icon: '🤖' };
              
              return (
                <div key={step.id} style={{ 
                  padding: '8px 12px',
                  background: '#f8f9fa',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '14px' }}>{info.icon}</span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '12px' }}>{step.agentId}</div>
                    <div style={{ fontSize: '10px', color: '#666' }}>{info.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Task Details Panel - Fixed width with proper constraints */}
      <div style={{ 
        width: '420px', 
        minWidth: '420px',
        maxWidth: '420px',
        flexShrink: 0
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
          Task Details
        </h3>
        
        {selectedStep ? (
          <TaskDetailsPanel taskData={selectedStep} />
        ) : (
          <div style={{ 
            padding: '40px 20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px solid #e0e0e0',
            height: 'fit-content'
          }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
              Select a pipeline step to view task details
            </div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              Click on any step in the workflow pipeline above
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// FIXED: Export the component properly
export const CompactWorkflowDetailView: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const initialTab = searchParams.get('tab') as 'design' | 'history' | 'execute' || 'design';
  const [activeTab, setActiveTab] = useState<'design' | 'history' | 'execute'>(initialTab);
  const [isExecuting, setIsExecuting] = useState(initialTab === 'execute');

  // Find the workflow
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(
    enhancedWorkflows.find(wf => wf.id === workflowId) || enhancedWorkflows[0]
  );

  useEffect(() => {
    if (workflowId) {
      const workflow = enhancedWorkflows.find(wf => wf.id === workflowId);
      setSelectedWorkflow(workflow || enhancedWorkflows[0]);
    }
  }, [workflowId]);

  const handleExecute = () => {
    setIsExecuting(true);
    setActiveTab('execute');
  };

  const handleWorkflowChange = (newWorkflowId: string) => {
    const workflow = enhancedWorkflows.find(wf => wf.id === newWorkflowId);
    setSelectedWorkflow(workflow || null);
    navigate(`/workflows/${newWorkflowId}?tab=${activeTab}`);
  };

  const handleTabChange = (newTab: 'design' | 'history' | 'execute') => {
    setActiveTab(newTab);
    if (selectedWorkflow) {
      navigate(`/workflows/${selectedWorkflow.id}?tab=${newTab}`);
    }
  };

  if (!selectedWorkflow) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <h2>Workflow not found</h2>
        <button onClick={() => navigate('/workflows')}>Back to Workflows</button>
      </div>
    );
  }

  return (
    <div style={{ background: '#f5f5f5', height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
      {/* Compact Header */}
      <div style={{ 
        background: 'white', 
        borderBottom: '1px solid #e0e0e0',
        padding: '12px 24px',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => navigate('/workflows')}
              style={{
                width: '32px',
                height: '32px',
                border: '1px solid #e0e0e0',
                background: 'white',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ArrowLeft size={16} />
            </button>
            
            <div style={{ color: '#666', fontSize: '14px' }}>Workflow Studio</div>
            <ChevronDown size={16} style={{ color: '#666' }} />
            
            <select
              value={selectedWorkflow.id}
              onChange={(e) => handleWorkflowChange(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '600',
                minWidth: '250px'
              }}
            >
              {enhancedWorkflows.map(workflow => (
                <option key={workflow.id} value={workflow.id}>{workflow.name}</option>
              ))}
            </select>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Full Tabs */}
            <div style={{ 
              display: 'flex', 
              background: '#f0f0f0', 
              borderRadius: '4px', 
              padding: '2px'
            }}>
              <button
                onClick={() => handleTabChange('design')}
                style={{
                  padding: '6px 16px',
                  border: 'none',
                  background: activeTab === 'design' ? 'white' : 'transparent',
                  borderRadius: '3px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Design
              </button>
              <button
                onClick={() => handleTabChange('history')}
                style={{
                  padding: '6px 16px',
                  border: 'none',
                  background: activeTab === 'history' ? 'white' : 'transparent',
                  borderRadius: '3px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                History
              </button>
              <button
                onClick={() => handleTabChange('execute')}
                style={{
                  padding: '6px 16px',
                  border: 'none',
                  background: activeTab === 'execute' ? 'white' : 'transparent',
                  borderRadius: '3px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Execute
              </button>
            </div>
            
            <button 
              onClick={handleExecute}
              style={{
                padding: '6px 16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {isExecuting ? (
                <>
                  <Pause size={14} />
                  Running
                </>
              ) : (
                <>
                  <PlayCircle size={14} />
                  Execute
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ 
          background: 'white', 
          margin: '20px',
          borderRadius: '8px', 
          height: 'calc(100% - 40px)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ padding: '20px', flex: 1, overflow: 'auto' }}>
            {activeTab === 'design' && <CompactDesignView workflow={selectedWorkflow} />}
            {activeTab === 'history' && <HistoryView workflow={selectedWorkflow} />}
            {activeTab === 'execute' && <CompactWorkflowExecutionView workflow={selectedWorkflow} />}
          </div>
        </div>
      </div>
    </div>
  );
};